import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import DirectusService from '@/core/directus/directus.service';
import LanguageCode from '@/locale/language-code';
import OpeningHoursCMSModel from '@/visa-application/models/OpeningHoursCMSModel';
import ApplicationCenterCMSModel from '@/visa-application/models/ApplicationCenterCMSModel';
import VacAvsCMSModel from '@/visa-application/models/VacAvsCMSModel';
import AddedValueServiceCMSModel from '@/visa-application/models/AddedValueServiceCMSModel';
import { Keywords } from '@/visa-application/models/KeywordsCMSModel';
import _ from 'lodash';
import CurrencyCMSModel from '@/visa-application/models/CurrencyCMSModel';
import { ON_DEMAND, MINIO_BUCKET_ADDRESS } from '@/constants';
import { defineCanonicalTag } from '@/shared/utils/utils';

@Component
export default class OpeningHours extends Vue {
  public applicationCenterCmsData: ApplicationCenterCMSModel = new ApplicationCenterCMSModel();
  public openingHoursCmsData: OpeningHoursCMSModel = new OpeningHoursCMSModel();
  public vacAvsCmsData: VacAvsCMSModel = new VacAvsCMSModel();
  public showPopup = false;
  public moreAvsInfoData: AddedValueServiceCMSModel = new AddedValueServiceCMSModel();
  public onDemandCurrency: CurrencyCMSModel = new CurrencyCMSModel();
  public onDemand = ON_DEMAND;
  public MINIO_BUCKET_ADDRESS = MINIO_BUCKET_ADDRESS;

  @Inject('directusService')
  private directusService: () => DirectusService;

  private residenceCountryCode: string;
  private issuer: string;
  private unwatchCurrentLanguage: Function;
  public keywords: Partial<Keywords> = {};

  public beforeRouteEnter(to, from, next) {
    defineCanonicalTag(to.params.residenceCountryCode, to.params.issuer, to.params.formGroupId, to.fullPath);

    next(vm => {
      vm.residenceCountryCode = to.params.residenceCountryCode;
      vm.issuer = to.params.issuer;
      vm.unwatchCurrentLanguage = vm.$store.watch(
        () => vm.$store.getters.currentLanguage,
        currentLanguage => {
          vm.getContent(currentLanguage);
        },
        { immediate: true }
      );
    });
  }

  public beforeDestroyed(): void {
    this.unwatchCurrentLanguage();
  }

  @Watch('$store.getters.currentLanguage', { deep: true })
  public onCurrentLanguageChanged(language: LanguageCode): void {
    this.keywords = this.$store.getters.keywords;
  }

  @Watch('$store.getters.keywords', { immediate: true })
  public onKeywordsChanged(keywords: Keywords): void {
    this.keywords = keywords;
  }

  public getContent(currentLanguage: LanguageCode): void {
    this.directusService()
      .getCollection<OpeningHoursCMSModel>(OpeningHoursCMSModel, 'opening_hours', ['*.*'], undefined, currentLanguage)
      .then((response: OpeningHoursCMSModel[]) => {
        this.openingHoursCmsData = response[0];
        this.$store.commit('metaInfo', this.openingHoursCmsData);
      });

    this.directusService()
      .getCollection<ApplicationCenterCMSModel>(
        ApplicationCenterCMSModel,
        'application_centers',
        ['*.*', 'opening_hours_avs.translation.*', 'opening_hours_avs.*'],
        { code: { eq: this.issuer } },
        currentLanguage
      )
      .then((result: ApplicationCenterCMSModel[]) => {
        this.applicationCenterCmsData = result[0];

        const filter = {
          'avs.sku': {
            eq: this.applicationCenterCmsData.opening_hours_avs.sku
          },
          'vac.code': {
            eq: this.issuer
          }
        };

        this.directusService()
          .getCollection<VacAvsCMSModel>(VacAvsCMSModel, 'vac_avs', ['*', 'currency.*', 'currency.translation.*'], filter, currentLanguage)
          .then((data: VacAvsCMSModel[]) => {
            this.vacAvsCmsData = data[0];
          })
          .catch(error => console.error(error));

        this.directusService()
          .getCollection<CurrencyCMSModel>(CurrencyCMSModel, 'currencies', ['*.*'], undefined, currentLanguage)
          .then((res: CurrencyCMSModel[]) => {
            this.onDemandCurrency = res.find(currency => currency.code === this.onDemand);
          })
          .catch(error => console.error(error));
      })
      .catch(error => console.error(error));
  }

  public moreAvsInfo(avs: AddedValueServiceCMSModel): void {
    this.moreAvsInfoData = avs;
    this.showPopup = true;
  }

  public closeAvsPopup(): void {
    this.showPopup = false;
  }
}
